import { ref, computed } from 'vue';
import { regenerateNote, targetKey } from '../api/draft';
import { getCalendarEvents } from '../api/calendar';
import { useQuery } from '@tanstack/vue-query';
import { useTriageStore } from '../stores/triage';
import type { TriageDraft } from '../stores/triage';
import { useTimezoneStore } from '../stores/timezone';
import { ApiClientError, getCurrentTimesheet } from '../api/client';
import { formatMonthDay, periodDayCount, weekdayAlignedDayIndex } from '../utils/period';
import type { TimesheetResponse } from '../api/client';
import type { ImportedEntry } from '../utils/timesheetCsv';
import type { PeriodItem } from '../api/periods';
import type { CalendarEventsResponse, SourceEvent, SourceId } from '../api/calendar';
import type { BillingTarget, DraftNote, DraftResponse, DraftRow, NeedsInputItem, NoteKind, RowConfidence } from '../api/draft';
import { sourceFacts, sourceLink, type SourceEventRef, type SourceFacts, type SourceLink as SourceLinkModel } from '../components/sourceFacts';

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export interface PickerEdit {
  eventId: string;
  dayIndex: number;
  hours: number;
  signatureHash?: string;
  // The cell's existing note, staged at ctx-build time so a re-target carries
  // it to the new row instead of silently dropping it (edit standard, note 6).
  comment?: string;
  commentKind?: NoteKind;
}
// The attribution wizard's session (ADR-0042). 'create' is a brand-new manual
// row; 'resolve' is a needs-input item (its edits carry the real eventId +
// signature so the pick leaves the queue and records against memory); 'edit'
// re-targets an existing row (project/phase/task all change through the same
// progressive picker — inline editing can't stand in for the phased cascade).
export interface WizardCtx {
  mode: 'create' | 'resolve' | 'edit';
  contextLabel: string;
  edits?: PickerEdit[];
  // Resolve mode surfaces the item's candidate targets (the agent's suggestions)
  // under a "Suggested" heading, above the user's recents.
  suggested?: BillingTarget[];
  // The user's recent targets, offered as ready picks below the suggestions.
  recent?: BillingTarget[];
  // Resolve mode: the carried note pre-filled into the wizard's comment. The
  // source's own note wins over the agent's phrasing — the user wrote it, about
  // their own time, on the day — and commentHint names where it came from.
  initialComment?: string;
  commentHint?: string;
  // Resolve mode: what the source knows about the item (tags, attendance),
  // shown as a strip under the wizard's title, and the item's address in the
  // source, shown beside the title.
  facts?: SourceFacts | null;
  link?: SourceLinkModel | null;
  // Edit mode: the row to re-target + its current leaf (pre-seeds the picker),
  // and the events its time came from.
  rowKey?: string;
  initialTarget?: BillingTarget;
  events?: SourceEventRef[];
}

export interface CellDetailCtx {
  target: BillingTarget;
  dayIndex: number;
  dayLabel: string;
  hours: number;
  noteKind: string;
  reasoning: string;
  attributionSource?: import('../api/draft').AttributionSource;
  lessSure: string[];
  // Row-level confidence + confirmed state — the card surfaces the confidence in
  // words and the "why" for an uncertain cell, and clears both once confirmed.
  confidence: RowConfidence;
  confirmed?: boolean;
  anchor: HTMLElement | null;
  edits: PickerEdit[];
  isPhased?: boolean;
  // The cell holds nothing yet — the card is creating it, so there is nothing
  // to remove and the note has no source events to regenerate from.
  isNew?: boolean;
  // The row's other days, offered as something to copy onto this cell.
  copySources: CopySource[];
}

// One day of the row the cell card can copy from. The comment is the part worth
// not retyping; the hours come with it.
export interface CopySource {
  dayIndex: number;
  label: string;
  hours: number;
  note: string;
}

export interface UseTriageActionsOptions {
  effectiveDraft: () => DraftResponse | null;
  // The raw engine draft (no overlay) — the Triage drawer renders needs-input
  // items from it so resolved/dismissed cards stay visible and reversible.
  rawDraft: () => DraftResponse | null;
  activePeriodStart: () => string;
  activePeriodEnd: () => string;
  // The open periods VP offers — the set "Copy from…" chooses a source from.
  periods: () => PeriodItem[];
  highlightedWbs: { value: string | undefined };
  // VP RequireComments ≠ 'N' — a confirm that lands a noteless cell queues an
  // agent-drafted note (which satisfies the requirement and stays editable).
  commentRequired: () => boolean;
}

export function useTriageActions(opts: UseTriageActionsOptions) {
  const triageStore = useTriageStore();
  const tzStore = useTimezoneStore();

  // ---------------------------------------------------------------------------
  // Grid row helpers
  // ---------------------------------------------------------------------------
  function rowKeyParts(rowKey: string): { wbs1: string; wbs2: string; wbs3: string; category: string } {
    const [wbs1 = '', wbs2 = '', wbs3 = '', category = ''] = rowKey.split('|');
    return { wbs1, wbs2, wbs3, category };
  }

  // A grid row is keyed by its target's identity (see PreviewRow.rowKey), so the
  // row this key came from is the one whose target keys the same way.
  function findDraftRow(rowKey: string) {
    return opts.effectiveDraft()?.rows.find(r => targetKey(r.target) === rowKey);
  }

  function onRowClick(rowKey: string): void {
    const { wbs1 } = rowKeyParts(rowKey);
    opts.highlightedWbs.value = opts.highlightedWbs.value === wbs1 ? undefined : wbs1;
  }

  // ---------------------------------------------------------------------------
  // Overlay write helpers — every confirm path funnels through the same shape.
  // ---------------------------------------------------------------------------
  // A manual entry's overlay id: the full leaf plus the day, so the same project
  // can land as separate lines per phase/task and per day (Bug 5956).
  function manualEventId(t: BillingTarget, dayIndex: number): string {
    return `manual:${t.wbs1}|${t.wbs2}|${t.wbs3}|${dayIndex}`;
  }

  // Spread `next` across a cell's entries in their current proportions; the
  // remainder lands on the last so the total is exact.
  function scaleAcross(entries: { hours: number }[], next: number): number[] {
    const old = entries.reduce((s, e) => s + e.hours, 0);
    if (!(next > 0) || next === old) return entries.map(e => e.hours);
    let allocated = 0;
    return entries.map((e, i) => {
      const hours = i === entries.length - 1
        ? Math.round((next - allocated) * 100) / 100
        : Math.round((old > 0 ? (e.hours / old) * next : next / entries.length) * 100) / 100;
      allocated += hours;
      return hours;
    });
  }

  function targetFields(t: BillingTarget) {
    return {
      wbs1: t.wbs1,
      wbs1Name: t.wbs1Name,
      wbs2: t.wbs2,
      wbs2Name: t.wbs2Name,
      wbs3: t.wbs3,
      wbs3Name: t.wbs3Name,
      ...(t.category ? { category: t.category } : {}),
    };
  }

  // The engine's note for a (row, day) cell — undefined when blank.
  function engineNoteFor(row: DraftRow, dayIndex: number): DraftNote | undefined {
    const note = row.days.find(d => d.dayIndex === dayIndex)?.note;
    return note && note.text.trim() ? note : undefined;
  }

  // Note to write on a confirm: an existing overlay comment wins (the user's
  // last word — an explicit clear ('') included), else the engine note when
  // supplied (re-target paths pass it so the moved cell keeps its comment;
  // same-target paths omit it — reconcile keeps showing the engine note there
  // without an overlay copy).
  function carryComment(eventId: string, engineNote?: DraftNote): { comment?: string; commentKind?: NoteKind } {
    const existing = triageStore.getDraft(eventId);
    if (existing && existing.comment !== undefined) {
      return { comment: existing.comment, commentKind: existing.commentKind ?? 'edited' };
    }
    if (engineNote) return { comment: engineNote.text, commentKind: engineNote.kind };
    return {};
  }

  function carryLaborCode(eventId: string): { laborCode?: string } {
    const laborCode = triageStore.getDraft(eventId)?.laborCode;
    return laborCode ? { laborCode } : {};
  }

  // ---------------------------------------------------------------------------
  // Row field edit (project / phase / task on an existing row)
  //
  // Editing any field re-picks the whole project → phase → task chain — a phase
  // change re-scopes the task, a project change re-scopes both — so a single
  // inline pick can't stand in. All three fields open the wizard in edit mode,
  // pre-seeded to the row's current leaf (openRowEditWizard, below). Whole-row
  // scope: the confirm re-targets every contributing event, carrying its note.
  // ---------------------------------------------------------------------------

  // Grid cell clicks. The anchor/field the grid passes are vestigial now the
  // editor is a centered modal that shows all three fields at once.
  function onProjectCellClick(rowKey: string): void { openRowEditWizard(rowKey); }
  function onPhaseCellClick(rowKey: string): void { openRowEditWizard(rowKey); }

  // ---------------------------------------------------------------------------
  // Undo the last changes (#6542, deepened by #6549)
  // ---------------------------------------------------------------------------
  // A snapshot of the overlay taken before each write, put back on demand. Every
  // write funnels through triageStore.setDraft, so this covers all of them with no
  // per-action inverse — and it restores what a write *replaced*, which an inverse
  // could not: setDraft overwrites a whole entry, and there is no per-event delete
  // to reverse one it added.
  //
  // A bounded stack, oldest dropped past the bound. Depth is a shallow Map copy
  // per write, whose entries are shared with the store, so ten of them cost the
  // ten maps and nothing else.
  const UNDO_DEPTH = 10;
  const undoStack = ref<{ label: string; snapshot: Map<string, TriageDraft> }[]>([]);
  const undoable = computed(() => undoStack.value.at(-1) ?? null);

  function withUndo(label: string, write: () => void): void {
    const snapshot = new Map(triageStore.drafts);
    write();
    undoStack.value = [...undoStack.value, { label, snapshot }].slice(-UNDO_DEPTH);
  }

  function undoLastChange(): void {
    const last = undoStack.value.at(-1);
    if (!last) return;
    triageStore.restore(last.snapshot);
    undoStack.value = undoStack.value.slice(0, -1);
  }

  // A snapshot describes the overlay against one engine draft over one period. A
  // regenerate or a period switch replaces what every snapshot was taken against,
  // so the whole stack goes rather than restoring entries into a sheet that never
  // held them.
  function forgetUndo(): void {
    undoStack.value = [];
  }

  // Re-target the whole row to the picked target, carrying each cell's note so
  // the move never drops a comment (edit standard rules 1 & 2).
  function applyTargetToRow(rowKey: string, target: BillingTarget): void {
    const row = findDraftRow(rowKey);
    if (!row) return;
    withUndo('Moved', () => retargetRow(row, target));
  }

  function retargetRow(row: DraftRow, target: BillingTarget): void {
    const noteless: PickerEdit[] = [];
    for (const e of row.contributingEvents) {
      const carried = carryComment(e.eventId, engineNoteFor(row, e.dayIndex));
      triageStore.setDraft({
        eventId: e.eventId,
        status: 'confirmed',
        ...targetFields(target),
        hours: e.hours,
        dayIndex: e.dayIndex,
        signatureHash: e.signatureHash,
        ...carried,
        ...carryLaborCode(e.eventId),
      });
      if (!carried.comment) noteless.push({ eventId: e.eventId, dayIndex: e.dayIndex, hours: e.hours, signatureHash: e.signatureHash });
    }
    queueAutoNotes(target, noteless);
  }


  // ---------------------------------------------------------------------------
  // Attribution wizard state (create + resolve)
  // ---------------------------------------------------------------------------
  const wizardOpen = ref(false);
  const wizardCtx = ref<WizardCtx | null>(null);
  // The row a wizard confirm just created (resolve / add) — the grid flashes it
  // so the user sees where it landed. The host clears it after the animation.
  // Every cell the confirm wrote — a grouped resolve lands one row across
  // several days, and highlighting only the first would hide most of the work
  // the user just committed to.
  const justAddedRow = ref<{ wbs1: string; dayIndexes: number[] } | null>(null);

  const recentTargets = computed((): BillingTarget[] => {
    const seen = new Set<string>();
    const out: BillingTarget[] = [];
    for (const r of opts.effectiveDraft()?.rows ?? []) {
      const k = `${r.target.wbs1}${r.target.wbs2}${r.target.wbs3}`;
      if (!seen.has(k)) { seen.add(k); out.push(r.target); }
    }
    return out;
  });

  function lessSureFrom(c: RowConfidence): string[] {
    const out: string[] = [];
    if (c.target !== 'high') out.push('Which project this belongs to');
    if (c.hours !== 'high') out.push('How many hours');
    return out;
  }

  function dayLabelFor(dayIndex: number): string {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(opts.activePeriodStart());
    if (!m) return `Day ${dayIndex + 1}`;
    const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
    d.setUTCDate(d.getUTCDate() + dayIndex);
    return `${DOW[d.getUTCDay()]} ${formatMonthDay(d.toISOString().slice(0, 10))}`;
  }

  // ---------------------------------------------------------------------------
  // Calendar events query — gated on cell detail being open
  // ---------------------------------------------------------------------------
  const cellDetailOpen = ref(false);

  // Fires once the draft has rows (warming the shared cache — opening the
  // Triage drawer or the cell-detail modal is then a cache hit, not a refetch;
  // both share the ['calendar-events', from, to, tz] key).
  const calendarQuery = useQuery<CalendarEventsResponse, ApiClientError>({
    queryKey: computed(() => ['calendar-events', opts.activePeriodStart(), opts.activePeriodEnd(), tzStore.selectedZone]),
    queryFn: () => getCalendarEvents(opts.activePeriodStart() || undefined, opts.activePeriodEnd() || undefined, tzStore.selectedZone),
    enabled: computed(() => cellDetailOpen.value || (opts.effectiveDraft()?.rows.length ?? 0) > 0),
    retry: false,
    staleTime: 60_000,
  });

  // eventId → source id. The draft's own contributing events win: the backend
  // recorded the source at draft time, so provenance still shows when the event
  // no longer resolves on a fresh read. The live read fills in the rest.
  // The live read, keyed for the wizard's facts strip and its comment pre-fill.
  const eventsById = computed((): Map<string, SourceEvent> =>
    new Map((calendarQuery.data.value?.events ?? []).map(e => [e.id, e])));

  const sourceByEventId = computed((): Map<string, SourceId> => {
    const m = new Map<string, SourceId>();
    for (const e of calendarQuery.data.value?.events ?? []) m.set(e.id, e.source);
    for (const row of opts.effectiveDraft()?.rows ?? []) {
      for (const ce of row.contributingEvents) {
        if (ce.source) m.set(ce.eventId, ce.source);
      }
    }
    return m;
  });

  // ---------------------------------------------------------------------------
  // Cell detail state + handlers
  // ---------------------------------------------------------------------------
  const cellDetailCtx = ref<CellDetailCtx | null>(null);
  const cellDetailNote = ref('');
  const regeneratingNote = ref(false);
  const regenerateError = ref('');

  // What the live read knows about each contributing event, for the rundowns in
  // the cell card and the edit wizard.
  function eventRefs(events: { eventId: string; hours: number }[]): SourceEventRef[] {
    return events.map(e => {
      const ev = eventsById.value.get(e.eventId);
      return {
        eventId: e.eventId,
        hours: e.hours,
        subject: ev?.subject,
        start: ev?.start,
        source: sourceByEventId.value.get(e.eventId),
        link: sourceLink(ev),
      };
    });
  }

  // Manual entries have no source event behind them, so they list nothing —
  // otherwise the card offers a rundown of "Event details unavailable" and a
  // Regenerate that has nothing to read.
  const cellDetailEvents = computed((): SourceEventRef[] =>
    eventRefs((cellDetailCtx.value?.edits ?? []).filter(e => !e.eventId.startsWith('manual:'))));

  function onCellBadgeClick(rowKey: string, dayIndex: number, anchor: HTMLElement): void {
    const row = findDraftRow(rowKey);
    if (!row) return;
    const cell = row.days.find(d => d.dayIndex === dayIndex);
    const cellEvents = row.contributingEvents.filter(e => e.dayIndex === dayIndex);
    // An untouched day on the row has no events to scale, so the card edits one
    // new manual entry — the same shape the wizard's create path writes.
    const isNew = cellEvents.length === 0;
    cellDetailCtx.value = {
      target: row.target,
      dayIndex,
      dayLabel: dayLabelFor(dayIndex),
      hours: cell?.hours ?? 0,
      noteKind: isNew ? 'user' : (cell?.note.kind ?? 'agent'),
      reasoning: row.reasoning,
      attributionSource: row.attributionSource,
      lessSure: lessSureFrom(row.confidence),
      confidence: row.confidence,
      confirmed: row.confirmed,
      anchor,
      edits: isNew
        ? [{ eventId: manualEventId(row.target, dayIndex), dayIndex, hours: 0 }]
        : cellEvents.map(e => ({ eventId: e.eventId, dayIndex, hours: e.hours, signatureHash: e.signatureHash })),
      isPhased: row.isPhased,
      isNew,
      copySources: copySourcesFor(row, dayIndex),
    };
    cellDetailNote.value = cell?.note.text ?? '';
    regenerateError.value = '';
    cellDetailOpen.value = true;
  }

  async function onRegenerateNote(): Promise<void> {
    const ctx = cellDetailCtx.value;
    if (!ctx || regeneratingNote.value) return;
    regenerateError.value = '';
    if (ctx.edits.length === 0) {
      regenerateError.value = 'Nothing to regenerate — this entry has no source calendar events.';
      return;
    }
    regeneratingNote.value = true;
    try {
      const res = await regenerateNote({
        target: ctx.target,
        eventRefs: ctx.edits.map(e => e.eventId),
        from: opts.activePeriodStart() || undefined,
        to: opts.activePeriodEnd() || undefined,
        tz: tzStore.selectedZone || undefined,
      });
      if (res.note.text.trim()) {
        // Fill the staged note in the card; it commits with Save, not now.
        cellDetailNote.value = res.note.text;
      } else {
        regenerateError.value = 'No note was generated — the note agent may be unavailable, or this entry\'s source events no longer exist.';
      }
    } catch (e) {
      regenerateError.value = 'Couldn\'t regenerate the note. Check your connection and try again.';
      console.error('[CellDetail] regenerate note failed', e);
    } finally {
      regeneratingNote.value = false;
    }
  }

  // Save the staged cell edits (edit standard rule 3 — this cell's events only):
  // re-target, scale hours to the new total, and write the note, in one pass.
  // The remainder of a scaled total lands on the last event so the sum is exact.
  function onCellDetailSave(payload: { target: BillingTarget; hours: number; note: string; noteKind: string }): void {
    const ctx = cellDetailCtx.value;
    if (!ctx || ctx.edits.length === 0) return;
    const { target } = payload;
    const noteText = payload.note.trim();
    const scaled = scaleAcross(ctx.edits, Math.round(payload.hours * 100) / 100);
    const noteless: PickerEdit[] = [];
    withUndo(ctx.isNew ? 'Added' : 'Edited', () => ctx.edits.forEach((e, i) => {
      const hours = scaled[i];
      // A cell being created keys on whatever target the card ends up on, not
      // the row it was opened from — a re-target there is a different entry, and
      // keeping the opening row's key would overwrite the one it moved to.
      const eventId = ctx.isNew ? manualEventId(target, e.dayIndex) : e.eventId;
      triageStore.setDraft({
        eventId,
        status: 'confirmed',
        ...targetFields(target),
        hours,
        dayIndex: e.dayIndex,
        signatureHash: e.signatureHash,
        comment: noteText,
        commentKind: payload.noteKind as NoteKind,
        ...carryLaborCode(eventId),
      });
      e.hours = hours;
      if (!noteText) noteless.push({ eventId, dayIndex: e.dayIndex, hours, signatureHash: e.signatureHash });
    }));
    // A comment-required cell saved without a note queues an agent draft
    // (queueAutoNotes self-gates on the tenant's RequireComments setting).
    if (!noteText) queueAutoNotes(target, noteless);
    opts.highlightedWbs.value = target.wbs1;
    cellDetailOpen.value = false;
  }

  // Removing a cell sends its events back to the needs-input queue for a fresh
  // pick. A manual entry has no source event to return to, so it is dropped.
  function onCellDetailRemove(): void {
    const ctx = cellDetailCtx.value;
    if (!ctx) return;
    withUndo('Removed', () => {
      for (const e of ctx.edits) {
        triageStore.setDraft({
          eventId: e.eventId,
          status: e.eventId.startsWith('manual:') ? 'rejected' : 'unassigned',
          hours: e.hours,
          dayIndex: e.dayIndex,
          signatureHash: e.signatureHash,
        });
      }
    });
    cellDetailOpen.value = false;
  }

  // The row's other filled days, ordered the way the card offers them: the day
  // just gone first, then further back, then forward — a repeat nearly always
  // means the most recent day like this one.
  function copySourcesFor(row: DraftRow, dayIndex: number): CopySource[] {
    return row.days
      .filter(d => d.dayIndex !== dayIndex && d.hours > 0)
      .sort((a, b) => {
        const aBefore = a.dayIndex < dayIndex;
        if (aBefore !== (b.dayIndex < dayIndex)) return aBefore ? -1 : 1;
        return aBefore ? b.dayIndex - a.dayIndex : a.dayIndex - b.dayIndex;
      })
      .map(d => ({
        dayIndex: d.dayIndex,
        label: dayLabelFor(d.dayIndex),
        hours: d.hours,
        note: d.note.text,
      }));
  }

  // Copy another of the row's days onto the open cell. A cell that already holds
  // hours asks first — the copy overwrites both the hours and the comment, which
  // Undo restores but the user cannot see coming.
  const pendingCellCopy = ref<CopySource | null>(null);

  function copyCellFrom(dayIndex: number): void {
    const ctx = cellDetailCtx.value;
    const source = ctx?.copySources.find(s => s.dayIndex === dayIndex);
    if (!ctx || !source) return;
    if (ctx.hours > 0) pendingCellCopy.value = source;
    else applyCellCopy(source);
  }

  function applyCellCopy(source: CopySource): void {
    const ctx = cellDetailCtx.value;
    if (!ctx) return;
    const rows = opts.effectiveDraft()?.rows ?? [];
    withUndo('Copied', () => {
      bookCell(rows, ctx.target, ctx.dayIndex, source.hours, source.note, 'replace');
    });
    opts.highlightedWbs.value = ctx.target.wbs1;
    justAddedRow.value = { wbs1: ctx.target.wbs1, dayIndexes: [ctx.dayIndex] };
    pendingCellCopy.value = null;
    cellDetailOpen.value = false;
  }

  function confirmCellCopy(): void {
    if (pendingCellCopy.value) applyCellCopy(pendingCellCopy.value);
  }

  // Remove a whole draft row: every cell it holds, in one undo entry rather than
  // one per cell. Its events go where a cleared cell's events go — a source event
  // back to the queue, a manual entry rejected, since nothing outside the overlay
  // would ever propose it again.
  function removeRow(rowKey: string): void {
    const row = findDraftRow(rowKey);
    if (!row) return;
    withUndo('Removed row', () => {
      for (const ce of row.contributingEvents) {
        triageStore.setDraft({
          eventId: ce.eventId,
          status: ce.eventId.startsWith('manual:') ? 'rejected' : 'unassigned',
          hours: ce.hours,
          dayIndex: ce.dayIndex,
          signatureHash: ce.signatureHash,
        });
      }
    });
  }

  // ---------------------------------------------------------------------------
  // Needs-input resolver — stages a blank draft row and opens the wizard in
  // resolve mode (ADR-0042 §2). Searches the raw engine draft so a resolved item
  // can be re-opened from the Triage drawer (re-pick wins).
  // ---------------------------------------------------------------------------
  function openResolveWizard(item: NeedsInputItem): void {
    const hours = triageStore.getDraft(item.eventId)?.hours ?? item.hours ?? 0;
    const dayIndex = item.dayIndex ?? 0;
    // Candidates and recents stay separate so the picker heads them "Suggested"
    // and "Recent"; the picker dedups a target that is both (candidate wins).
    const event = eventsById.value.get(item.eventId);
    // A re-queued item, or one on a draft opened without a source read, carries
    // no subject — the live read has it.
    const label = item.subject || event?.subject || item.eventId;
    wizardCtx.value = {
      mode: 'resolve',
      contextLabel: `${label}${hours ? ` · ${hours}h` : ''}`,
      edits: [{ eventId: item.eventId, dayIndex, hours, signatureHash: item.signatureHash }],
      suggested: item.candidateTargets,
      recent: recentTargets.value,
      initialComment: event?.sourceNote || item.note,
      // Only Freshdesk records a note the user wrote about their own time, so
      // the hint can name it outright.
      commentHint: event?.sourceNote ? 'From the ticket note' : '',
      facts: sourceFacts(event, 'strip'),
      link: sourceLink(event),
    };
    wizardOpen.value = true;
  }

  // A grouped series resolved in one pass: the wizard opens once and its
  // confirm loops the edits, so every event takes the same leaf and comment
  // while keeping its own day, hours and signature.
  function openGroupWizard(items: NeedsInputItem[]): void {
    if (items.length === 0) return;
    const hours = items.reduce(
      (sum, i) => sum + (triageStore.getDraft(i.eventId)?.hours ?? i.hours ?? 0), 0);
    wizardCtx.value = {
      mode: 'resolve',
      contextLabel: `${items[0].subject ?? items[0].eventId} · ${items.length} events`
        + (hours ? ` · ${Math.round(hours * 100) / 100}h` : ''),
      edits: items.map(i => ({
        eventId: i.eventId,
        dayIndex: i.dayIndex ?? 0,
        hours: triageStore.getDraft(i.eventId)?.hours ?? i.hours ?? 0,
        signatureHash: i.signatureHash,
      })),
      // The agent's candidates across the group, first occurrence wins.
      suggested: [...new Map(
        items.flatMap(i => i.candidateTargets).map(t => [t.wbs1 + t.wbs2 + t.wbs3, t]),
      ).values()],
      recent: recentTargets.value,
      // One comment covers the series, so a per-event source note would be a
      // half-truth about the rest — the agent's shared note stands.
      initialComment: items.find(i => i.note)?.note,
      facts: sourceFacts(eventsById.value.get(items[0].eventId), 'strip'),
      link: sourceLink(eventsById.value.get(items[0].eventId)),
    };
    wizardOpen.value = true;
  }

  // The inspector passes an anchor for the old popover; the wizard is a centered
  // modal, so it is ignored.
  function onPickProject(eventId: string): void {
    const item = opts.effectiveDraft()?.needsInput.find(i => i.eventId === eventId)
      ?? opts.rawDraft()?.needsInput.find(i => i.eventId === eventId);
    if (item) openResolveWizard(item);
  }

  // Promote an agent-skipped event: the inspector synthesizes a needs-input-
  // shaped item and the wizard resolves it the same way (§2, ux-batch-2026-07).
  function onPromoteFiltered(item: NeedsInputItem): void {
    openResolveWizard(item);
  }

  // Dismiss a needs-input item directly (it has no attributed row to search —
  // unlike dismissEvent below, which walks contributingEvents). A second
  // dismiss undoes it ('proposed' doesn't override the engine view), so the
  // Triage drawer's dismissed cards stay reversible.
  function onDismissNeedsInputItem(eventId: string): void {
    const item = opts.rawDraft()?.needsInput.find(i => i.eventId === eventId)
      ?? opts.effectiveDraft()?.needsInput.find(i => i.eventId === eventId);
    if (!item) return;
    const undoing = triageStore.getDraft(eventId)?.status === 'rejected';
    triageStore.setDraft({
      eventId: item.eventId,
      status: undoing ? 'proposed' : 'rejected',
      dayIndex: item.dayIndex,
      signatureHash: item.signatureHash,
    });
  }

  // ---------------------------------------------------------------------------
  // Comment auto-generation (Item 2/5, ux-batch-2026-07) — fire-and-forget a
  // note-regenerate call for events confirmed without a note, landing the
  // result as an agent-attributed overlay comment (editable via cell detail).
  // Failure leaves the comment empty; it never blocks the confirm itself.
  // ---------------------------------------------------------------------------
  const regeneratingNoteEventIds = ref<Set<string>>(new Set());

  async function autoGenerateNotes(target: BillingTarget, edits: PickerEdit[]): Promise<void> {
    const eventIds = edits.map(e => e.eventId);
    for (const id of eventIds) regeneratingNoteEventIds.value.add(id);
    try {
      const res = await regenerateNote({
        target,
        eventRefs: eventIds,
        from: opts.activePeriodStart() || undefined,
        to: opts.activePeriodEnd() || undefined,
        tz: tzStore.selectedZone || undefined,
      });
      const text = res.note.text.trim();
      if (!text) return;
      for (const e of edits) {
        const existing = triageStore.getDraft(e.eventId);
        // Only patch a still-confirmed pick — a dismiss/re-pick in the interim
        // must win over a stale auto-note.
        if (!existing || existing.status !== 'confirmed') continue;
        triageStore.setDraft({ ...existing, comment: text, commentKind: 'agent' });
      }
    } catch (e) {
      console.error('[Triage] auto note generate failed', e);
    } finally {
      for (const id of eventIds) regeneratingNoteEventIds.value.delete(id);
    }
  }

  // RequireComments (note 6): a confirm that lands noteless cells queues an
  // agent-drafted note. Manual entries have no source events to draft from —
  // their note is typed in the wizard (always required there).
  function queueAutoNotes(target: BillingTarget, edits: PickerEdit[]): void {
    if (!opts.commentRequired()) return;
    const real = edits.filter(e => !e.eventId.startsWith('manual:'));
    if (real.length > 0) void autoGenerateNotes(target, real);
  }

  // ---------------------------------------------------------------------------
  // Per-event quick actions (C3) — confirm / dismiss a single attributed event
  // from the EventsInspector. Both write through the same overlay path as the
  // pickers: confirm re-affirms the event's current row target; dismiss sets
  // status 'rejected' (the exact string reconcileDraft.overridesView drops on).
  // ---------------------------------------------------------------------------
  /** The row and contribution carrying an event, in whichever draft is asked. */
  function findContribution(draft: DraftResponse | null, eventId: string) {
    for (const row of draft?.rows ?? []) {
      const ce = row.contributingEvents.find(e => e.eventId === eventId);
      if (ce) return { row, ce };
    }
    return null;
  }

  /**
   * Undo a dismissal.
   *
   * Reads the engine draft, not the reconciled one: a rejection removes the
   * contribution from the reconciled draft, and that contribution is exactly
   * what the undo needs, so reading it there finds nothing and the undo does
   * silently nothing. A dismissed held card has no row in either — returning it
   * to the queue is its undo.
   */
  function undoDismiss(eventId: string): void {
    const found = findContribution(opts.rawDraft(), eventId);
    if (found) {
      confirmEvent(eventId, found);
      return;
    }
    const item = opts.rawDraft()?.needsInput.find(i => i.eventId === eventId);
    if (!item) return;
    triageStore.setDraft({
      eventId,
      status: 'unassigned',
      hours: item.hours,
      dayIndex: item.dayIndex,
      signatureHash: item.signatureHash,
    });
  }

  function confirmEvent(
    eventId: string,
    from?: NonNullable<ReturnType<typeof findContribution>>,
  ): void {
    for (const row of from ? [from.row] : opts.effectiveDraft()?.rows ?? []) {
      const ce = from?.ce ?? row.contributingEvents.find(e => e.eventId === eventId);
      if (!ce) continue;
      const carried = carryComment(ce.eventId);
      triageStore.setDraft({
        eventId: ce.eventId,
        status: 'confirmed',
        ...targetFields(row.target),
        hours: ce.hours,
        dayIndex: ce.dayIndex,
        signatureHash: ce.signatureHash,
        ...carried,
        ...carryLaborCode(ce.eventId),
      });
      if (!carried.comment && !engineNoteFor(row, ce.dayIndex)) {
        queueAutoNotes(row.target, [{ eventId: ce.eventId, dayIndex: ce.dayIndex, hours: ce.hours, signatureHash: ce.signatureHash }]);
      }
      return;
    }
  }

  // "Use anyway" on a Duplicate. The in-VP mark is a shape match on project,
  // day and hours — a guess — and this is the user saying the work is genuinely
  // separate. Confirms the cell at its existing target and flags the override,
  // which reconcileDraft honours so the row returns to the draft and saves.
  function useAnyway(eventId: string): void {
    for (const row of opts.effectiveDraft()?.rows ?? []) {
      const ce = row.contributingEvents.find(e => e.eventId === eventId);
      if (!ce) continue;
      triageStore.setDraft({
        eventId: ce.eventId,
        status: 'confirmed',
        ...targetFields(row.target),
        hours: ce.hours,
        dayIndex: ce.dayIndex,
        signatureHash: ce.signatureHash,
        useAnyway: true,
        ...carryComment(ce.eventId),
        ...carryLaborCode(ce.eventId),
      });
      return;
    }

    // A held card the app recorded as published has no row to confirm against,
    // so the override returns it to the queue for a fresh pick. The flag has to
    // persist either way: the server keeps sending the mark until VP's period
    // reads empty.
    const item = opts.rawDraft()?.needsInput.find(i => i.eventId === eventId);
    if (item) {
      triageStore.setDraft({
        eventId,
        status: 'unassigned',
        hours: item.hours,
        dayIndex: item.dayIndex,
        signatureHash: item.signatureHash,
        useAnyway: true,
      });
      return;
    }
  }

  function dismissEvent(eventId: string): void {
    for (const row of opts.effectiveDraft()?.rows ?? []) {
      const ce = row.contributingEvents.find(e => e.eventId === eventId);
      if (!ce) continue;
      triageStore.setDraft({
        eventId: ce.eventId,
        status: 'rejected',
        dayIndex: ce.dayIndex,
        signatureHash: ce.signatureHash,
      });
      return;
    }
  }

  // ---------------------------------------------------------------------------
  // Attribution wizard confirm / cancel (create + resolve) and the "+ Add"
  // opener. Confirm always carries a comment (the wizard requires one).
  // ---------------------------------------------------------------------------
  function openAddWizard(): void {
    wizardCtx.value = { mode: 'create', contextLabel: 'Add time' };
    wizardOpen.value = true;
  }

  // Row field edit → the wizard pre-seeded to the row's current leaf. Confirm
  // re-targets the whole row (applyTargetToRow); the comment is untouched (it's
  // per-cell, edited in the cell card), so edit mode shows no comment field.
  function openRowEditWizard(rowKey: string): void {
    const row = findDraftRow(rowKey);
    if (!row) return;
    const total = row.contributingEvents.reduce((s, e) => s + e.hours, 0);
    wizardCtx.value = {
      mode: 'edit',
      contextLabel: `${row.target.wbs1Name || row.target.wbs1} · ${total.toFixed(2)}h`,
      rowKey,
      initialTarget: row.target,
      recent: recentTargets.value,
      events: eventRefs(row.contributingEvents),
    };
    wizardOpen.value = true;
  }

  // ---------------------------------------------------------------------------
  // Repeat — the create wizard's picked days, booked in one pass
  // ---------------------------------------------------------------------------
  const pendingRepeat = ref<{
    target: BillingTarget;
    comment: string;
    hours: number;
    dayIndexes: number[];
  } | null>(null);
  const repeatClashOpen = ref(false);

  // Picked days the target already carries hours on. A second entry there would
  // sum into a doubled cell (reconcileDraft step 3), so the user settles them.
  const repeatClashes = computed((): { dayIndex: number; dayLabel: string; hours: number }[] => {
    const p = pendingRepeat.value;
    const row = p && opts.effectiveDraft()?.rows.find(r => targetKey(r.target) === targetKey(p.target));
    if (!p || !row) return [];
    const clashes = [];
    for (const dayIndex of p.dayIndexes) {
      const cell = row.days.find(d => d.dayIndex === dayIndex);
      if (cell) clashes.push({ dayIndex, dayLabel: dayLabelFor(dayIndex), hours: cell.hours });
    }
    return clashes;
  });

  // Book one cell as a manual entry, or re-hour what it already holds. A second
  // entry beside an occupied cell would land it at the sum of both
  // (reconcileDraft step 3), so 'replace' re-hours the cell's own contributing
  // events instead. Returns false where an occupied cell was left alone.
  //
  // `rows` is snapshotted by the caller: booking mutates the overlay, and
  // re-reading the reconciled draft per cell would both re-run the projection
  // for every one and read a sheet the user never agreed to.
  function bookCell(
    rows: DraftRow[],
    target: BillingTarget,
    dayIndex: number,
    hours: number,
    comment: string,
    onClash: 'skip' | 'replace',
  ): boolean {
    const row = rows.find(r => targetKey(r.target) === targetKey(target));
    const occupied = !!row?.days.some(d => d.dayIndex === dayIndex);
    if (occupied && onClash === 'skip') return false;

    const existing = occupied ? row!.contributingEvents.filter(e => e.dayIndex === dayIndex) : [];
    if (existing.length === 0) {
      triageStore.setDraft({
        eventId: manualEventId(target, dayIndex),
        status: 'confirmed',
        ...targetFields(target),
        hours,
        dayIndex,
        comment,
        commentKind: 'user',
      });
      return true;
    }

    const scaled = scaleAcross(existing, hours);
    existing.forEach((e, i) => triageStore.setDraft({
      eventId: e.eventId,
      status: 'confirmed',
      ...targetFields(target),
      hours: scaled[i],
      dayIndex,
      signatureHash: e.signatureHash,
      comment,
      commentKind: 'user',
      ...carryLaborCode(e.eventId),
    }));
    return true;
  }

  function applyRepeat(onClash: 'skip' | 'replace'): void {
    const p = pendingRepeat.value;
    if (!p) return;
    const rows = opts.effectiveDraft()?.rows ?? [];
    let written: number[] = [];
    withUndo('Added', () => {
      written = p.dayIndexes.filter(
        dayIndex => bookCell(rows, p.target, dayIndex, p.hours, p.comment, onClash));
    });
    opts.highlightedWbs.value = p.target.wbs1;
    justAddedRow.value = { wbs1: p.target.wbs1, dayIndexes: written };
    pendingRepeat.value = null;
    repeatClashOpen.value = false;
  }

  function cancelRepeat(): void {
    pendingRepeat.value = null;
    repeatClashOpen.value = false;
  }

  // ---------------------------------------------------------------------------
  // Copy from another period (#6541) — a period's saved VP sheet, re-laid on
  // this one by weekday and booked as manual entries.
  // ---------------------------------------------------------------------------
  const copyOpen = ref(false);
  const copySourcePeriodEnd = ref('');
  const copyExcludeLeave = ref(true);

  // Every period but the one being drafted — copying a period onto itself does
  // nothing but re-book what is already there.
  const copyablePeriods = computed(
    () => opts.periods().filter(p => p.endDate !== opts.activePeriodEnd()));

  function openCopyFromPeriod(): void {
    const earlier = copyablePeriods.value
      .filter(p => p.endDate < opts.activePeriodEnd())
      .sort((a, b) => b.endDate.localeCompare(a.endDate));
    copySourcePeriodEnd.value = earlier[0]?.endDate ?? copyablePeriods.value[0]?.endDate ?? '';
    copyExcludeLeave.value = true;
    copyOpen.value = true;
  }

  function cancelCopy(): void {
    copyOpen.value = false;
  }

  // Shares the period key the grid's own VP read uses, so a period already
  // looked at is copied from cache.
  const copySourceQuery = useQuery<TimesheetResponse, ApiClientError>({
    queryKey: computed(() => ['timesheet', copySourcePeriodEnd.value]),
    queryFn: () => getCurrentTimesheet(copySourcePeriodEnd.value),
    enabled: computed(() => copyOpen.value && !!copySourcePeriodEnd.value),
    retry: false,
    staleTime: 30_000,
  });

  const copySourceLoading = computed(() => copySourceQuery.isFetching.value);

  // The tenant's time categories by WBS path. A saved VP row carries no
  // category of its own — VP resolved the alias onto its WBS at save — so this
  // is the only thing that tells a category row from a project row.
  const categoriesByPath = computed((): Map<string, BillingTarget[]> => {
    const byPath = new Map<string, BillingTarget[]>();
    for (const c of opts.effectiveDraft()?.categories ?? []) {
      const path = `${c.wbs1}|${c.wbs2}|${c.wbs3}`;
      byPath.set(path, [...(byPath.get(path) ?? []), c]);
    }
    return byPath;
  });

  const categoriesFor = (row: { wbs1: string; wbs2: string; wbs3: string }) =>
    categoriesByPath.value.get(`${row.wbs1}|${row.wbs2}|${row.wbs3}`);

  // Leave is the one kind of category worth leaving behind — Admin, Training and
  // the rest are ordinary recurring work that a fortnight repeats like anything
  // else. VP flags none of them as absence (a TimeCategory is a code and a name),
  // so the name is the only signal, matched whole-word as EventRelevance.cs does
  // on the API side. It fails towards copying: an unrecognised name comes across,
  // which the user can delete, rather than going missing.
  const readsAsLeave = (categories: BillingTarget[] | undefined) =>
    !!categories?.some(c => /\b(holiday|pto|vacation|sick|leave|absence)\b/i.test(c.wbs1Name || c.wbs1));

  const copySourceHasLeaveRows = computed(() => (copySourceQuery.data.value?.rows ?? [])
    .some(r => readsAsLeave(categoriesFor(r))));

  interface CopyCell { target: BillingTarget; dayIndex: number; hours: number; comment: string }

  const copyPlan = computed((): CopyCell[] => {
    const sheet = copySourceQuery.data.value;
    if (!sheet) return [];
    const dayCount = periodDayCount(opts.activePeriodStart(), opts.activePeriodEnd());
    const plan: CopyCell[] = [];
    for (const row of sheet.rows) {
      const categories = categoriesFor(row);
      if (copyExcludeLeave.value && readsAsLeave(categories)) continue;
      const target: BillingTarget = {
        wbs1: row.wbs1, wbs1Name: row.wbs1Name,
        wbs2: row.wbs2, wbs2Name: row.wbs2Name,
        wbs3: row.wbs3, wbs3Name: row.wbs3Name,
        // One matching category names the row; several are indistinguishable
        // from the WBS alone, so it copies as the project row VP holds.
        ...(categories?.length === 1 ? { category: categories[0].category } : {}),
      };
      for (const cell of row.days) {
        if (!(cell.total > 0)) continue;
        // VP numbers a sheet's day columns from one.
        const dayIndex = weekdayAlignedDayIndex(
          sheet.periodStart, cell.index - 1, opts.activePeriodStart(), dayCount);
        if (dayIndex === null) continue;
        plan.push({ target, dayIndex, hours: cell.total, comment: cell.comment });
      }
    }
    // Weekday alignment scrambles the source's order, so sort into the order
    // the grid reads in — the plan is listed to the user before it is written.
    return plan.sort((a, b) => a.dayIndex - b.dayIndex);
  });

  const copyTotalHours = computed(() => copyPlan.value.reduce((sum, c) => sum + c.hours, 0));

  // What the copy would land on top of, rolled up per project. Skip and Replace
  // act on the whole copy, so the question is how much each row stands to lose,
  // not which cell — and a per-cell list repeats one project name a dozen times.
  const copyClashes = computed((): { label: string; days: number; hours: number }[] => {
    const rows = opts.effectiveDraft()?.rows ?? [];
    const byTarget = new Map<string, { label: string; days: number; hours: number }>();
    for (const c of copyPlan.value) {
      const cell = rows.find(r => targetKey(r.target) === targetKey(c.target))
        ?.days.find(d => d.dayIndex === c.dayIndex);
      if (!cell) continue;
      const group = byTarget.get(targetKey(c.target))
        ?? { label: c.target.wbs1Name || c.target.wbs1, days: 0, hours: 0 };
      group.days += 1;
      group.hours = Math.round((group.hours + cell.hours) * 100) / 100;
      byTarget.set(targetKey(c.target), group);
    }
    return [...byTarget.values()];
  });

  const copyClashCount = computed(() => copyClashes.value.reduce((n, c) => n + c.days, 0));
  const copyClashHours = computed(() => copyClashes.value.reduce((h, c) => h + c.hours, 0));

  // Copied rows are appended, never merged into what Vantagepoint already
  // holds (Bug 5956), so a target period with saved hours ends up at the sum.
  const copyTargetHasVpHours = computed(() => (opts.effectiveDraft()?.rows ?? [])
    .some(r => r.days.some(d => d.written === 'in-vp')));

  function applyCopy(onClash: 'skip' | 'replace'): void {
    const rows = opts.effectiveDraft()?.rows ?? [];
    const plan = copyPlan.value;
    withUndo('Copied', () => {
      for (const c of plan) {
        bookCell(rows, c.target, c.dayIndex, c.hours, c.comment, onClash);
      }
    });
    copyOpen.value = false;
  }

  // CSV import (#6961): the entries the import plan kept, booked like a copy —
  // manual confirmed edits under one Undo step, saved through the usual path.
  function applyImport(entries: ImportedEntry[], onClash: 'skip' | 'replace'): void {
    if (entries.length === 0) return;
    const rows = opts.effectiveDraft()?.rows ?? [];
    withUndo('Imported', () => {
      for (const e of entries) bookCell(rows, e.target, e.dayIndex, e.hours, e.comment, onClash);
    });
  }

  function onWizardConfirm(target: BillingTarget, comment: string, entry?: { dayIndexes: number[]; hours: number }): void {
    const ctx = wizardCtx.value;
    if (!ctx) return;
    if (ctx.mode === 'edit') {
      // Re-target the existing row; per-cell notes ride along (applyTargetToRow).
      if (ctx.rowKey) applyTargetToRow(ctx.rowKey, target);
      opts.highlightedWbs.value = target.wbs1;
    } else if (ctx.mode === 'resolve') {
      // Write a confirmed overlay for the item's event, carrying its real
      // signature so reconcileDraft moves it into the grid and drops it from
      // needs-input (memory records against the right signature). A category
      // pick books a standard day, not the event's clock span (ADR-0044) —
      // mirrors the engine's rule for agent-confirmed category rows.
      const hoursFor = (h: number) =>
        target.category ? (opts.effectiveDraft()?.standardDayHours ?? 8) : h;
      for (const e of ctx.edits ?? []) {
        triageStore.setDraft({
          eventId: e.eventId,
          status: 'confirmed',
          ...targetFields(target),
          hours: hoursFor(e.hours),
          dayIndex: e.dayIndex,
          signatureHash: e.signatureHash,
          comment,
          commentKind: 'user',
        });
      }
      opts.highlightedWbs.value = target.wbs1;
      justAddedRow.value = {
        wbs1: target.wbs1,
        dayIndexes: [...new Set((ctx.edits ?? []).map(e => e.dayIndex))].sort((a, b) => a - b),
      };
    } else {
      // Create: one manual overlay entry for the picked leaf on each picked day,
      // all carrying the one comment. Days the target already has hours on are
      // named first — the dialog decides whether they are skipped or replaced.
      pendingRepeat.value = {
        target,
        comment,
        hours: entry?.hours ?? 0,
        dayIndexes: entry?.dayIndexes ?? [0],
      };
      if (repeatClashes.value.length > 0) repeatClashOpen.value = true;
      else applyRepeat('skip');
    }
    wizardOpen.value = false;
    wizardCtx.value = null;
  }

  function onWizardCancel(): void {
    wizardOpen.value = false;
    wizardCtx.value = null;
  }

  return {
    // Grid interactions
    rowKeyParts,
    findDraftRow,
    onRowClick,
    // Row field edit (project / phase / task on a row) → wizard edit mode
    onProjectCellClick,
    onPhaseCellClick,
    applyTargetToRow,
    // Bulk confirm + per-event quick actions (C3)
    confirmEvent,
    undoDismiss,
    useAnyway,
    dismissEvent,
    recentTargets,
    // Cell detail
    cellDetailOpen,
    cellDetailCtx,
    cellDetailNote,
    cellDetailEvents,
    regeneratingNote,
    regenerateError,
    onCellBadgeClick,
    // Cell-level copy from another day on the same row
    copyCellFrom,
    pendingCellCopy,
    confirmCellCopy,
    onRegenerateNote,
    onCellDetailSave,
    onCellDetailRemove,
    removeRow,
    // Needs-input → wizard
    onPickProject,
    onPromoteFiltered,
    onDismissNeedsInputItem,
    regeneratingNoteEventIds,
    // Attribution wizard
    wizardOpen,
    wizardCtx,
    justAddedRow,
    openAddWizard,
    onWizardConfirm,
    openGroupWizard,
    onWizardCancel,
    // Repeat clash (Skip / Replace / Cancel)
    repeatClashOpen,
    repeatClashes,
    applyRepeat,
    cancelRepeat,
    // Copy from another period
    copyOpen,
    copyablePeriods,
    copySourcePeriodEnd,
    copyExcludeLeave,
    copySourceHasLeaveRows,
    copySourceLoading,
    copyPlan,
    copyTotalHours,
    copyClashes,
    copyClashCount,
    copyClashHours,
    copyTargetHasVpHours,
    openCopyFromPeriod,
    applyCopy,
    cancelCopy,
    // CSV import
    applyImport,
    // Undo the last change (#6542)
    undoable,
    undoLastChange,
    forgetUndo,
  };
}
